'use client';

import { useMemo, useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@/lib/table-features';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState,
  useQueryStates
} from 'nuqs';
import type { ExtendedColumnSort } from '@/types/data-table';
import { getSortingStateParser } from '@/lib/parsers';
import { buildDynamicFormColumns } from '@/lib/build-dynamic-form-columns';
import {
  Camera,
  Check,
  ClipboardCheck,
  FileText,
  PlayCircle,
  ScanLine
} from 'lucide-react';

import { toast } from 'sonner';
import { useDataTable } from '@/hooks/use-data-table';
import { useFilteredColumns } from '@/hooks/use-filtered-columns';
import { useHardwareScanner } from '@/hooks/use-hardware-scanner';
import { useHashDialog } from '@/hooks/use-hash-dialog';
import { useQrNavigateSearch } from '@/hooks/use-qr-navigate-search';
import { QrScanDialog } from '@/components/qr-scan-dialog';
import { useSelectAllPages } from '@/hooks/use-select-all-pages';
import { useRouter } from '@/i18n/routing';
import { cn } from '@/lib/utils';
import { IS_QR_SCANNER_ENABLED } from '@/lib/feature-flags';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { ListingSkeleton } from '@/components/listing-skeleton';
import { DataViewToggle, useDataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SortControl } from '@/components/sort-control';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import {
  HighlightMatch,
  getColumnSearchTerms
} from '@/components/highlight-match';
import { SelectAllBanner } from '@/components/select-all-banner';
import {
  registrationQueries,
  type Registration,
  type RegistrationsListParams
} from '@/features/registration';
import { dynamicFormQueries, type DynamicForm } from '@/features/dynamic-forms';

import { REVIEW_HASH } from '../constants';
import { ReviewTableActionBar } from './review-table-action-bar';
import { ReviewCard } from './review-card';
import { ReviewCompactRow } from './review-compact-row';
import {
  SubStatusCell,
  getEffectiveStage,
  getEffectiveStagePillStyle
} from '@/features/registration';
import { stageFilterOptions, useWorkflowStages } from '@/features/workflow';

export function ReviewListing() {
  const pipeline = useWorkflowStages();
  const t = useTranslations('review');
  const tc = useTranslations('common');
  const tReg = useTranslations('registration');
  const router = useRouter();
  const [view, setView] = useDataView('review');
  const [scannerActive, setScannerActive] = useState(true);
  // Camera scan dialog open-state lives in the URL hash (#scan) so it is
  // deep-linkable and closes on browser Back.
  const { open: scanDialogOpen, onOpenChange: toggleScanDialog } =
    useHashDialog(REVIEW_HASH.scan);
  const [hwScan, setHwScan] = useState<{ value: string; key: number } | null>(
    null
  );

  const { results: hwScanResults, isFetching: hwScanFetching } =
    useQrNavigateSearch({
      searchInput: hwScan?.value ?? '',
      stage: 'review',
      enabled: !!hwScan
    });

  useEffect(() => {
    if (!hwScan || hwScanFetching) return;
    if (hwScanResults.length > 0) {
      router.push(`/dashboard/review/${hwScanResults[0].id}`);
    } else {
      toast.error(t('notFound'));
    }
  }, [hwScanResults, hwScanFetching, hwScan, router, t]);

  useHardwareScanner({
    onScan: (value) => setHwScan({ value, key: Date.now() }),
    enabled: scannerActive && !scanDialogOpen
  });

  // ─── Dynamic form selector ──────────────────────────────────────────
  const { data: activeTemplate } = useQuery(
    dynamicFormQueries.activeByType('registration')
  );
  const { data: allFormsData } = useQuery(
    dynamicFormQueries.list({
      type: 'registration',
      status: 'active',
      per_page: 100
    })
  );
  const allForms = (allFormsData?.data ?? []) as DynamicForm[];

  const [urlFormId, setUrlFormId] = useQueryState(
    'dynamic_form_id',
    parseAsString.withDefault('')
  );
  const activeFormId = activeTemplate?.data?.id;
  const isAllSelected = urlFormId === 'all';

  const displayFormId = isAllSelected
    ? activeFormId
    : urlFormId
      ? Number(urlFormId)
      : activeFormId;

  const apiFormId = isAllSelected
    ? undefined
    : urlFormId
      ? Number(urlFormId)
      : activeFormId;

  const { data: selectedFormData } = useQuery({
    ...dynamicFormQueries.detail(displayFormId ?? 0),
    enabled: !!displayFormId && displayFormId !== activeFormId
  });

  const selectedForm =
    displayFormId === activeFormId
      ? activeTemplate?.data
      : selectedFormData?.data;
  const dynamicFormFields = useMemo(
    () => selectedForm?.fields ?? [],
    [selectedForm?.fields]
  );
  const dynamicColumns = useMemo(
    () => buildDynamicFormColumns(dynamicFormFields, { group: 'review' }),
    [dynamicFormFields]
  );
  const toolbarKey = useMemo(
    () => dynamicColumns.map((c) => c.id).join(',') || 'base',
    [dynamicColumns]
  );

  const selectionFields = useMemo(
    () => dynamicFormFields.filter((f) => f.type === 'selection_list'),
    [dynamicFormFields]
  );
  const dynamicFieldParsers = useMemo(
    () =>
      Object.fromEntries(
        selectionFields.map((f) => [
          `form_field_${f.id}`,
          parseAsArrayOf(parseAsString, ',').withDefault([])
        ])
      ),
    [selectionFields]
  );
  const [dynamicFieldFilters] = useQueryStates(dynamicFieldParsers);

  const textFields = useMemo(
    () =>
      dynamicFormFields.filter(
        (f) =>
          f.type === 'short_text' ||
          f.type === 'long_text' ||
          f.type === 'phone' ||
          f.type === 'email'
      ),
    [dynamicFormFields]
  );

  const dateFields = useMemo(
    () => dynamicFormFields.filter((f) => f.type === 'date'),
    [dynamicFormFields]
  );
  const dynamicDateParsers = useMemo(
    () =>
      Object.fromEntries(
        dateFields.map((f) => [
          `form_field_${f.id}`,
          parseAsString.withDefault('')
        ])
      ),
    [dateFields]
  );
  const [dynamicDateFilters] = useQueryStates(dynamicDateParsers);

  const columns = useMemo<ColumnDef<Registration>[]>(() => {
    const searchPlaceholder =
      textFields.length > 0
        ? `${t('searchPlaceholder')}, ${textFields.map((f) => f.label).join(', ')}...`
        : `${t('searchPlaceholder')}...`;
    return [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label='Select all'
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label='Select row'
          />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 40
      },
      {
        id: 'container_number',
        accessorKey: 'container_number',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('containerNumber')} />
        ),
        cell: ({ row, column }) => (
          <button
            type='button'
            className='text-primary flex items-center gap-1 font-medium hover:underline'
            onClick={() =>
              router.push(
                `/dashboard/containers/${row.original.id}?stage=review`
              )
            }
          >
            <span className='text-primary'>↗</span>
            <HighlightMatch
              text={row.original.container_number}
              query={getColumnSearchTerms(column)}
            />
          </button>
        ),
        enableColumnFilter: true,
        meta: {
          label: searchPlaceholder,
          variant: 'text' as const,
          placeholder: searchPlaceholder,
          permission: 'review.column.containerNumber'
        }
      },
      ...dynamicColumns,
      {
        id: 'current_stage',
        accessorKey: 'current_stage',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={tc('currentStage')} />
        ),
        cell: ({ row }) => {
          const effectiveStage = getEffectiveStage(
            row.original.current_stage,
            row.original.stage_status
          );
          const stageLabels: Record<string, string> = {
            registration: tc('stageRegistration'),
            sticking: tc('stageSticking'),
            review: tc('stageReview'),
            ready_for_packing: tc('stageReadyForPacking'),
            shipping: tc('stageShipping'),
            reception: tReg('stageReception'),
            sorting: tReg('stageSorting'),
            handover_scanning: tReg('stageHandoverScanning'),
            photo_scanning: tReg('stagePhotoScanning'),
            qr_review: tReg('stageQrReview'),
            warehouse: tReg('stageWarehouse'),
            ocr: tReg('stageOcr'),
            qa1_ocr: tReg('stageQa1Ocr'),
            qa2_ocr: tReg('stageQa2Ocr'),
            qa3_ocr: tReg('stageQa3Ocr'),
            archived: tReg('stageArchived')
          };
          if (effectiveStage === 'archived') return null;
          return (
            <Badge
              variant='secondary'
              className={cn(getEffectiveStagePillStyle(effectiveStage))}
            >
              {stageLabels[effectiveStage] ?? effectiveStage}
            </Badge>
          );
        },
        enableSorting: false,
        enableColumnFilter: true,
        meta: {
          label: tc('currentStage'),
          variant: 'multiSelect' as const,
          icon: 'solar:routing-bold-duotone',
          options: stageFilterOptions(
            pipeline.stages,
            (key) => tReg(key),
            'review'
          ),
          permission: 'review.column.currentStage'
        }
      },
      {
        id: 'subStatus',
        accessorKey: 'stage_status',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={tReg('subStatus')} />
        ),
        cell: ({ row }) => (
          <SubStatusCell
            stageStatus={row.original.stage_status}
            physicalStage={row.original.physical_stage}
          />
        ),
        enableSorting: false,
        enableColumnFilter: true,
        meta: {
          label: tReg('filterStatus'),
          variant: 'multiSelect' as const,
          icon: 'solar:checklist-minimalistic-bold-duotone',
          options: [
            {
              label: tReg('stageStatus.ready_for_review'),
              value: 'ready_for_review'
            },
            {
              label: tReg('stageStatus.returned_for_review'),
              value: 'returned_for_review'
            },
            { label: tReg('stageStatus.reviewed'), value: 'reviewed' }
          ],
          permission: 'review.column.subStatus'
        }
      },
      // {
      //   id: 'batch_start_page',
      //   header: () => (
      //     <div className='text-center text-xs'>{t('firstPage')}</div>
      //   ),
      //   cell: ({ row }) => {
      //     const reg = row.original;
      //     if (!reg.latest_batch_id) return <div className='text-center'>—</div>;
      //     return (
      //       <div className='text-center text-sm'>
      //         {reg.latest_batch_start_page}
      //       </div>
      //     );
      //   },
      //   enableSorting: false,
      //   size: 80
      // },
      // {
      //   id: 'batch_end_page',
      //   header: () => (
      //     <div className='text-center text-xs'>{t('lastPage')}</div>
      //   ),
      //   cell: ({ row }) => {
      //     const reg = row.original;
      //     if (!reg.latest_batch_id) return <div className='text-center'>—</div>;
      //     return (
      //       <div className='flex items-center justify-center gap-1.5'>
      //         <span className='text-sm'>{reg.latest_batch_end_page}</span>
      //         <Badge
      //           variant='secondary'
      //           className='bg-green-100 px-1.5 py-0 text-[10px] text-green-800 dark:bg-green-900 dark:text-green-200'
      //         >
      //           <Check className='me-0.5 h-2.5 w-2.5' />
      //           {t('saved')}
      //         </Badge>
      //       </div>
      //     );
      //   },
      //   enableSorting: false,
      //   size: 100
      // },
      {
        id: 'is_rejected',
        accessorFn: (row) =>
          row.rejected_to_stage !== null ? 'true' : 'false',
        header: () => null,
        cell: () => null,
        size: 0,
        enableColumnFilter: true,
        enableSorting: false,
        enableHiding: false,
        meta: {
          label: tc('filterRejectionStatus'),
          variant: 'select' as const,
          icon: 'solar:close-circle-bold-duotone',
          options: [
            { label: tc('filterRejected'), value: 'true' },
            { label: tc('filterNotRejected'), value: 'false' }
          ]
        }
      },
      {
        id: 'actions',
        header: () => <div className='text-end'>{t('actions')}</div>,
        cell: ({ row }) => {
          const isCompleted = row.original.review_completed_at !== null;
          return (
            <div className='flex items-center justify-end gap-1'>
              {isCompleted ? (
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() =>
                    router.push(`/dashboard/review/${row.original.id}`)
                  }
                >
                  {t('viewContainer')}
                </Button>
              ) : (
                <Button
                  variant='default'
                  size='sm'
                  className='bg-green-600 text-white hover:bg-green-700'
                  onClick={() =>
                    router.push(`/dashboard/review/${row.original.id}`)
                  }
                >
                  <PlayCircle className='me-1.5 h-4 w-4' />
                  {t('startReview')}
                </Button>
              )}
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false
      }
    ];
  }, [t, tc, tReg, router, dynamicColumns, textFields, pipeline.stages]);

  const [urlPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [urlPerPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [urlSearch] = useQueryState(
    'container_number',
    parseAsString.withDefault('')
  );
  const [urlPriority] = useQueryState(
    'priority',
    parseAsString.withDefault('')
  );
  const [urlStage] = useQueryState(
    'current_stage',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlIsRejected] = useQueryState(
    'is_rejected',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlSubStatus] = useQueryState(
    'subStatus',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlSorting] = useQueryState(
    'sort',
    getSortingStateParser<Registration>().withDefault([
      { id: 'created_at', desc: true }
    ] as ExtendedColumnSort<Registration>[])
  );

  const queryParams = useMemo<RegistrationsListParams>(() => {
    const params: RegistrationsListParams = {
      page: urlPage,
      per_page: urlPerPage,
      current_stage: urlStage.length > 0 ? urlStage.join(',') : 'review'
    };
    if (urlSearch) {
      params.search = urlSearch;
    }
    if (urlPriority) {
      params.priority = urlPriority;
    }
    if (apiFormId) {
      params.dynamic_form_id = apiFormId;
    }
    params.sort_by = urlSorting[0]?.id ?? 'created_at';
    params.sort_direction = urlSorting[0]?.desc ? 'desc' : 'asc';

    if (urlIsRejected.includes('true')) {
      params.is_rejected = true;
    } else if (urlIsRejected.includes('false')) {
      params.is_rejected = false;
    }

    if (urlSubStatus.length > 0) {
      params.stage_status = urlSubStatus.join(',');
    }

    for (const [key, values] of Object.entries(dynamicFieldFilters)) {
      if (Array.isArray(values) && values.length > 0) {
        const fieldId = key.replace('form_field_', '');
        (params as Record<string, unknown>)[
          `form_responses_filter[${fieldId}]`
        ] = values[0];
      }
    }

    if (urlSearch && textFields.length > 0) {
      (params as Record<string, unknown>)['search_text_field_ids'] = textFields
        .map((f) => f.id)
        .join(',');
    }

    for (const [key, value] of Object.entries(dynamicDateFilters)) {
      if (value) {
        const fieldId = key.replace('form_field_', '');
        const parts = String(value).split(',').filter(Boolean);
        const toDateStr = (ts: string) => {
          const d = new Date(Number(ts));
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        };
        const from = parts[0] ? toDateStr(parts[0]) : null;
        const to = parts[1] ? toDateStr(parts[1]) : null;
        if (from)
          (params as Record<string, unknown>)[
            `form_date_filter[${fieldId}][from]`
          ] = from;
        if (to)
          (params as Record<string, unknown>)[
            `form_date_filter[${fieldId}][to]`
          ] = to;
      }
    }

    return params;
  }, [
    urlPage,
    urlPerPage,
    urlSearch,
    urlStage,
    urlPriority,
    urlIsRejected,
    urlSubStatus,
    apiFormId,
    urlSorting,
    dynamicFieldFilters,
    textFields,
    dynamicDateFilters
  ]);

  const { data, isPending, isPlaceholderData } = useQuery(
    registrationQueries.list(queryParams)
  );

  const containers = (data?.data ?? []) as Registration[];
  const pageCount = data?.pagination?.totalPages ?? 0;
  const pagination = data?.pagination;

  const filteredColumns = useFilteredColumns(columns);

  const { table } = useDataTable<Registration>({
    columns: filteredColumns,
    pageKey: 'review',
    data: containers,
    pageCount,
    initialState: {
      sorting: [{ id: 'created_at', desc: true }],
      pagination: { pageIndex: 0, pageSize: 10 }
    }
  });

  const selectAll = useSelectAllPages(table, pagination?.total ?? 0);
  const tableState = table.state;

  const sortOptions = useMemo(
    () => [
      { label: t('containerNumber'), value: 'container_number' },
      { label: t('createdAt'), value: 'created_at' }
    ],
    [t]
  );

  return (
    <div className='space-y-4'>
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex items-center gap-3'>
          <div className='flex items-center gap-2'>
            <FileText className='text-muted-foreground h-4 w-4' />
            <Select
              value={urlFormId || (activeFormId ? String(activeFormId) : '')}
              onValueChange={(v) => {
                if (v === String(activeFormId)) {
                  setUrlFormId(null);
                } else {
                  setUrlFormId(v || null);
                }
              }}
            >
              <SelectTrigger className='h-8 w-auto min-w-[200px] text-xs'>
                <SelectValue placeholder={t('registrationForm')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>{t('allForms')}</SelectItem>
                {allForms.map((form) => (
                  <SelectItem key={form.id} value={String(form.id)}>
                    {form.name}
                    {form.is_default && (
                      <span className='text-muted-foreground ms-1'>
                        ({t('defaultForm')})
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className='flex items-center gap-2'>
          {view !== 'table' && (
            <SortControl
              options={sortOptions}
              sorting={tableState.sorting}
              onSortingChange={(s) => table.setSorting(s)}
            />
          )}
          <DataViewToggle
            value={view}
            onValueChange={setView}
            pageKey='review'
          />
          {IS_QR_SCANNER_ENABLED && (
            <>
              <Button
                variant='outline'
                size='sm'
                onClick={() => {
                  setScannerActive((prev) => {
                    if (!prev) (document.activeElement as HTMLElement)?.blur();
                    return !prev;
                  });
                }}
              >
                <span className='relative me-2 flex h-2 w-2 shrink-0'>
                  <span
                    className={cn(
                      'absolute inline-flex h-full w-full rounded-full opacity-75',
                      scannerActive
                        ? 'animate-ping bg-green-400'
                        : 'bg-gray-400'
                    )}
                  />
                  <span
                    className={cn(
                      'relative inline-flex h-2 w-2 rounded-full',
                      scannerActive ? 'bg-green-500' : 'bg-gray-400'
                    )}
                  />
                </span>
                <ScanLine className='me-2 h-4 w-4' />
                {t('scanQR')}
              </Button>
              <Button
                variant='outline'
                size='icon'
                onClick={() => toggleScanDialog(true)}
                title={tc('scanCamera')}
              >
                <Camera className='h-4 w-4' />
              </Button>
            </>
          )}
        </div>
      </div>

      <DataTableToolbar
        key={toolbarKey}
        table={table}
        viewFilterPermission='review.view_filter'
      />

      {isPending ? (
        <ListingSkeleton view={view} />
      ) : containers.length === 0 && tableState.columnFilters.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-12 text-center'>
          <ClipboardCheck className='text-muted-foreground mb-4 h-12 w-12' />
          <p className='text-muted-foreground text-lg'>{t('noContainers')}</p>
        </div>
      ) : view === 'grid' ? (
        <>
          <div
            className={cn(
              'grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {containers.map((reg) => (
              <ReviewCard
                key={reg.id}
                registration={reg}
                dynamicFormFields={dynamicFormFields}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : view === 'compact' ? (
        <>
          <div
            className={cn(
              'space-y-2',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {containers.map((reg) => (
              <ReviewCompactRow key={reg.id} registration={reg} />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : (
        <div
          className={cn(
            'flex min-h-[500px] flex-col gap-2',
            isPlaceholderData && 'opacity-60 transition-opacity'
          )}
        >
          <SelectAllBanner
            isAllPagesSelected={selectAll.isAllPagesSelected}
            isCurrentPageFullySelected={selectAll.isCurrentPageFullySelected}
            currentPageCount={containers.length}
            totalCount={pagination?.total ?? 0}
            pageSize={tableState.pagination.pageSize}
            onSelectAllPages={() => selectAll.setAllPagesSelected(true)}
            onClearSelection={() => selectAll.reset()}
          />
          <DataTable
            table={table}
            actionBar={
              <ReviewTableActionBar
                table={table}
                isAllPagesSelected={selectAll.isAllPagesSelected}
                totalCount={pagination?.total ?? 0}
              />
            }
          />
        </div>
      )}
      <QrScanDialog
        open={scanDialogOpen}
        onOpenChange={toggleScanDialog}
        stage='review'
        onNavigate={(id) => router.push(`/dashboard/review/${id}`)}
        translationNamespace='review'
      />
    </div>
  );
}
